import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Wallet as WalletIcon, TrendingDown, PiggyBank, Lock, ArrowUpRight, Award, Banknote } from "lucide-react";
import { getPaginated, get } from "@/lib/api-client";
import { formatCurrency, formatDateTime } from "@/lib/format";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { StatusPill } from "@/components/ui/StatusPill";
import { Pagination } from "@/components/ui/Pagination";
import { StatsCard } from "./components/StatsCard";
import type { Wallet, LedgerEntry, RewardTransaction, WithdrawalRequest } from "@/types/wallet";

const ITEMS_PER_PAGE = 5; // Perfect number to fit nicely in the card

export function WalletPage() {
  const [ledgerPage, setLedgerPage] = useState(1);
  const [rewardPage, setRewardPage] = useState(1);
  const [withdrawalPage, setWithdrawalPage] = useState(1);

  const wallet = useQuery({
    queryKey: ["wallet"],
    queryFn: () => get<Wallet>("/wallet/wallet"),
  });
  
  const ledger = useQuery({
    queryKey: ["ledger", ledgerPage],
    queryFn: () => getPaginated<LedgerEntry>(`/ledger/ledger`, { page: ledgerPage, page_size: 10 }),
  });
  
  const rewards = useQuery({
    queryKey: ["rewards"],
    queryFn: () => get<RewardTransaction[]>("/rewards/rewards"),
  });
  
  const withdrawals = useQuery({
    queryKey: ["withdrawals"],
    queryFn: () => get<WithdrawalRequest[]>("/wallet/wallet/withdrawals/list"),
  });

  // Client-side pagination logic
  const rewardsData = rewards.data || [];
  const rewardNumPages = Math.max(1, Math.ceil(rewardsData.length / ITEMS_PER_PAGE));
  const currentRewards = rewardsData.slice((rewardPage - 1) * ITEMS_PER_PAGE, rewardPage * ITEMS_PER_PAGE);

  const withdrawalsData = withdrawals.data || [];
  const withdrawalNumPages = Math.max(1, Math.ceil(withdrawalsData.length / ITEMS_PER_PAGE));
  const currentWithdrawals = withdrawalsData.slice((withdrawalPage - 1) * ITEMS_PER_PAGE, withdrawalPage * ITEMS_PER_PAGE);

  return (
    <div className="space-y-6 lg:space-y-8 animate-fade-in pb-10">
      
      {/* 🌟 Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 rounded-[1.25rem] border border-surface-200 shadow-sm">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-surface-900 tracking-tight">Wallet Dashboard</h1>
          <p className="text-sm text-surface-500 mt-1 font-medium">
            Manage your funds securely with our four-bucket ledger system.
          </p>
        </div>
        <Link to="/dashboard/wallet/withdraw">
          <Button size="lg" className="w-full sm:w-auto shadow-md" leftIcon={<ArrowUpRight className="h-4.5 w-4.5" />}>
            Request Withdrawal
          </Button>
        </Link>
      </div>

      {/* 📊 Balance Cards */}
      {wallet.isLoading ? (
        <div className="grid gap-4 sm:gap-6 grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-[1.25rem]" />)}
        </div>
      ) : wallet.data && (
        <div className="grid gap-4 sm:gap-6 grid-cols-2 lg:grid-cols-4">
          <StatsCard 
            icon={PiggyBank}   
            label="Withdrawable" 
            tone="success"
            value={formatCurrency(wallet.data.withdrawable)}
            hint="Ready for cashout" 
          />
          <StatsCard 
            icon={WalletIcon}  
            label="Available" 
            tone="info"
            value={formatCurrency(wallet.data.available)}
            hint="Total usable balance" 
          />
          <StatsCard 
            icon={Lock}        
            label="Frozen" 
            tone="warning"
            value={formatCurrency(wallet.data.frozen)}
            hint="Pending review" 
          />
          <StatsCard 
            icon={TrendingDown} 
            label="Withdrawn" 
            tone="danger"
            value={formatCurrency(wallet.data.total_withdrawn)}
            hint="Lifetime cashed out" 
          />
        </div>
      )}

      {/* 📋 Split View: Rewards & Withdrawals */}
      <div className="grid gap-6 lg:gap-8 lg:grid-cols-2">
        
        {/* Recent Rewards Card */}
        <Card className="flex flex-col min-h-[420px] rounded-[1.25rem] shadow-sm border-surface-200">
          <CardHeader className="bg-surface-50/50 border-b border-surface-100 px-6 py-4">
            <CardTitle>
              <span className="flex items-center gap-2">
                <Award className="w-5 h-5 text-success-600" />
                Recent Rewards
              </span>
            </CardTitle>
          </CardHeader>
          <CardBody className="p-0 flex-1 flex flex-col">
            {rewards.isLoading ? (
              <div className="p-6 space-y-4">
                {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}
              </div>
            ) : rewardsData.length > 0 ? (
              <>
                <ul className="divide-y divide-surface-100 flex-1">
                  {currentRewards.map((r) => (
                    <li key={r.reward_id} className="px-6 py-4 flex items-center justify-between hover:bg-surface-50 transition-colors">
                      <div className="flex-1 min-w-0 pr-4">
                        <div className="text-[15px] font-bold text-surface-900 truncate">{r.rule_name}</div>
                        <div className="text-[11px] font-medium text-surface-500 uppercase tracking-wide mt-1">
                          {r.trigger_type.replace(/_/g, " ")} • {formatDateTime(r.created_at)}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-base font-black text-success-700">+{formatCurrency(r.amount)}</div>
                        <div className="mt-1"><StatusPill status={r.status} /></div>
                      </div>
                    </li>
                  ))}
                </ul>
                {/* Pagination pinned to bottom */}
                {rewardNumPages > 1 && (
                  <div className="border-t border-surface-100 bg-surface-50/30 mt-auto rounded-b-[1.25rem]">
                    <Pagination page={rewardPage} numPages={rewardNumPages} onChange={setRewardPage} />
                  </div>
                )}
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-8 opacity-60">
                <Award className="w-12 h-12 text-surface-300 mb-3" />
                <span className="text-sm font-semibold text-surface-600">No rewards earned yet.</span>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Withdrawals Card */}
        <Card className="flex flex-col min-h-[420px] rounded-[1.25rem] shadow-sm border-surface-200">
          <CardHeader className="bg-surface-50/50 border-b border-surface-100 px-6 py-4">
            <CardTitle>
              <span className="flex items-center gap-2">
                <Banknote className="w-5 h-5 text-brand-600" />
                Withdrawal History
              </span>
            </CardTitle>
          </CardHeader>
          <CardBody className="p-0 flex-1 flex flex-col">
            {withdrawals.isLoading ? (
              <div className="p-6 space-y-4">
                {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}
              </div>
            ) : withdrawalsData.length > 0 ? (
              <>
                <ul className="divide-y divide-surface-100 flex-1">
                  {currentWithdrawals.map((w) => (
                    <li key={w.id} className="px-6 py-4 flex items-center justify-between hover:bg-surface-50 transition-colors">
                      <div className="flex-1 min-w-0 pr-4">
                        <div className="text-[15px] font-bold text-surface-900">Request #{w.id}</div>
                        <div className="text-[11px] font-medium text-surface-500 uppercase tracking-wide mt-1">
                          {formatDateTime(w.created_at)}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-base font-black text-surface-900">{formatCurrency(w.amount)}</div>
                        <div className="mt-1"><StatusPill status={w.status} /></div>
                      </div>
                    </li>
                  ))}
                </ul>
                {/* Pagination pinned to bottom */}
                {withdrawalNumPages > 1 && (
                  <div className="border-t border-surface-100 bg-surface-50/30 mt-auto rounded-b-[1.25rem]">
                    <Pagination page={withdrawalPage} numPages={withdrawalNumPages} onChange={setWithdrawalPage} />
                  </div>
                )}
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-8 opacity-60">
                <Banknote className="w-12 h-12 text-surface-300 mb-3" />
                <span className="text-sm font-semibold text-surface-600">No withdrawals requested yet.</span>
              </div>
            )}
          </CardBody>
        </Card>

      </div>

      {/* 📖 Ledger Statement */}
      <Card className="rounded-[1.25rem] shadow-sm border-surface-200 overflow-hidden">
        <CardHeader className="bg-surface-50/50 border-b border-surface-100 px-6 py-5">
          <CardTitle>Immutable Ledger Statement</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {ledger.isLoading ? (
            <div className="p-6 space-y-4">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full rounded-lg" />)}
            </div>
          ) : ledger.data && ledger.data.results.length > 0 ? (
            <div className="w-full overflow-x-auto">
              <Table className="min-w-[800px]">
                <THead>
                  <TR className="bg-surface-50">
                    <TH className="py-4 px-6 text-[11px]">Ledger ID</TH>
                    <TH className="py-4 px-6 text-[11px]">Type</TH>
                    <TH className="py-4 px-6 text-[11px]">Description</TH>
                    <TH className="py-4 px-6 text-[11px] text-right">Debit</TH>
                    <TH className="py-4 px-6 text-[11px] text-right">Credit</TH>
                    <TH className="py-4 px-6 text-[11px] text-right">Balance</TH>
                    <TH className="py-4 px-6 text-[11px]">Date & Time</TH>
                  </TR>
                </THead>
                <tbody className="divide-y divide-surface-100">
                  {ledger.data.results.map((l) => (
                    <TR key={l.ledger_id} className="hover:bg-surface-50/80 transition-colors">
                      <TD className="px-6 py-4 font-mono text-[10px] text-brand-700 font-bold">{l.ledger_id}</TD>
                      <TD className="px-6 py-4"><StatusPill status={l.entry_type} /></TD>
                      <TD className="px-6 py-4 text-[13px] font-medium text-surface-800 max-w-[250px] truncate" title={l.description}>
                        {l.description}
                      </TD>
                      <TD className="px-6 py-4 text-right text-danger-600 font-mono text-[13px] font-bold">
                        {Number(l.debit) > 0 ? `-${formatCurrency(l.debit)}` : "—"}
                      </TD>
                      <TD className="px-6 py-4 text-right text-success-700 font-mono text-[13px] font-bold">
                        {Number(l.credit) > 0 ? `+${formatCurrency(l.credit)}` : "—"}
                      </TD>
                      <TD className="px-6 py-4 text-right font-black text-surface-900 text-[14px]">
                        {formatCurrency(l.balance_after)}
                      </TD>
                      <TD className="px-6 py-4 text-[11px] font-medium text-surface-500 whitespace-nowrap">
                        {formatDateTime(l.created_at)}
                      </TD>
                    </TR>
                  ))}
                </tbody>
              </Table>
              <div className="border-t border-surface-200 bg-surface-50/30 px-2 py-1">
                <Pagination page={ledger.data.page} numPages={ledger.data.num_pages} onChange={setLedgerPage} />
              </div>
            </div>
          ) : (
            <div className="py-16 text-center flex flex-col items-center opacity-60">
              <WalletIcon className="w-12 h-12 text-surface-300 mb-3" />
              <span className="text-sm font-semibold text-surface-600">No ledger entries yet.</span>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}